export const INDEX_CACHE_TTL_MS = 2 * 60 * 60 * 1000;
export const MAX_ORG_CACHE_ENTRIES = 10;

export interface OrgIndexCacheEntry<TRecord, TDrive> {
  records: TRecord[];
  transformed: Map<number, TDrive>;
  transformedOrdered: TDrive[];
  sizeBytes: number;
  updatedAt: number;
  lastAccessAt: number;
}

export const estimateCacheSizeBytes = <TRecord>(records: TRecord[]): number =>
  // Heuristic only: avoid expensive JSON serialization on large datasets.
  records.length * 256;

export const isStale = <TRecord, TDrive>(
  entry: OrgIndexCacheEntry<TRecord, TDrive>,
  now = Date.now()
): boolean => now - entry.lastAccessAt > INDEX_CACHE_TTL_MS;

export const setOrgCacheEntry = <TRecord, TDrive>(
  cache: Map<number, OrgIndexCacheEntry<TRecord, TDrive>>,
  orgId: number,
  records: TRecord[],
  transformed: Map<number, TDrive>,
  transformedOrdered: TDrive[],
  now = Date.now()
): void => {
  cache.set(orgId, {
    records,
    transformed,
    transformedOrdered,
    sizeBytes: estimateCacheSizeBytes(records),
    updatedAt: now,
    lastAccessAt: now,
  });
};

export const touchOrgCacheEntry = <TRecord, TDrive>(
  cache: Map<number, OrgIndexCacheEntry<TRecord, TDrive>>,
  orgId: number,
  now = Date.now()
): void => {
  const entry = cache.get(orgId);
  if (!entry) return;
  entry.lastAccessAt = now;
};

export const purgeStaleOrgCacheEntries = <TRecord, TDrive>(
  cache: Map<number, OrgIndexCacheEntry<TRecord, TDrive>>,
  protectedOrgId?: number,
  now = Date.now()
): void => {
  const toDelete: number[] = [];
  cache.forEach((entry, orgId) => {
    if (protectedOrgId === orgId) return;
    if (isStale(entry, now)) {
      toDelete.push(orgId);
    }
  });
  toDelete.forEach((orgId) => {
    cache.delete(orgId);
  });
};

export const enforceOrgCacheLimit = <TRecord, TDrive>(
  cache: Map<number, OrgIndexCacheEntry<TRecord, TDrive>>,
  protectedOrgId?: number
): void => {
  // Primary strategy: remove smallest entries first.
  while (cache.size > MAX_ORG_CACHE_ENTRIES) {
    const candidates = Array.from(cache.entries())
      .filter(([orgId]) => orgId !== protectedOrgId)
      .sort((a, b) => a[1].sizeBytes - b[1].sizeBytes);

    if (candidates.length === 0) break;
    cache.delete(candidates[0][0]);
  }
};

export const clearOrgCache = <TRecord, TDrive>(
  cache: Map<number, OrgIndexCacheEntry<TRecord, TDrive>>
): void => {
  cache.clear();
};
